import { useState, useCallback } from 'react';
import { X, ChevronRight, Mail } from 'lucide-react';

const DISMISSED_KEY = 'perch_auto_forward_cta_dismissed';
const MIN_BOOKINGS_TO_SHOW = 1;

interface AutoForwardCTAProps {
  bookingCount: number;
}

const PROVIDER_GUIDES: Array<{ name: string; steps: string }> = [
  { name: 'Gmail', steps: 'Settings → Forwarding → Add track@perchtravel.com → Verify → Apply to matching emails' },
  { name: 'Outlook', steps: 'Settings → Mail → Rules → New rule → Forward airline confirmations to track@perchtravel.com' },
  { name: 'Apple Mail', steps: 'Preferences → Rules → Add Rule → Forward airline confirmations to track@perchtravel.com' },
];

/**
 * One-time CTA shown after user's first successful booking.
 * Encourages setting up auto-forwarding so future bookings
 * are tracked without manual action.
 *
 * Appears below the booking list. Dismissed via × (persisted in localStorage).
 */
export function AutoForwardCTA({ bookingCount }: AutoForwardCTAProps) {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISSED_KEY) === '1'; } catch { return false; }
  });
  const [expanded, setExpanded] = useState(false);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch { /* noop */ }
  }, []);

  if (dismissed || bookingCount < MIN_BOOKINGS_TO_SHOW) return null;

  return (
    <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200/60 dark:border-stone-700/50 overflow-hidden">
      {/* Subtle coral accent bar at top */}
      <div className="h-px bg-gradient-to-r from-transparent via-[var(--coral,#F97066)] to-transparent opacity-40" />

      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className="mt-0.5 p-2 rounded-lg bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 flex-shrink-0">
              <Mail className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-stone-900 dark:text-stone-100 mb-1">
                Set Up Auto-Forwarding
              </h3>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                Forward your booking confirmations and every future trip gets tracked automatically.
              </p>

              {/* Prominent email address */}
              <div className="mt-3">
                <span className="ticket-ref">
                  track@perchtravel.com
                </span>
              </div>
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="p-1 text-stone-300 dark:text-stone-600 hover:text-stone-500 dark:hover:text-stone-400 transition-colors flex-shrink-0"
            aria-label="Dismiss auto-forwarding suggestion"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <button
          onClick={() => setExpanded(!expanded)}
          className="mt-4 flex items-center gap-1.5 text-sm text-stone-600 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
        >
          <ChevronRight
            className={`w-3.5 h-3.5 transition-transform duration-200 ${expanded ? 'rotate-90' : ''}`}
          />
          How to Set Up
        </button>

        <div
          className={`grid transition-all duration-200 ease-in-out ${
            expanded ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0'
          }`}
        >
          <div className="overflow-hidden">
            <div className="space-y-3">
              {PROVIDER_GUIDES.map((guide) => (
                <div key={guide.name} className="pl-4 border-l-2 border-stone-200 dark:border-stone-700">
                  <p className="text-sm font-medium text-stone-700 dark:text-stone-300">{guide.name}</p>
                  <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">{guide.steps}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
